import React from 'react'
import PropTypes from 'prop-types'
import { createComponent } from 'react-fela'
import debounce from 'lodash/debounce'

import SearchIcon from 'design-framework/common/icons/magnifying-glass.svg'
import CancelIcon from 'design-framework/common/icons/close-cross.svg'
import Icon from 'design-framework/common/ui/Icon'
import errorIcon from 'design-framework/common/icons/input-error.svg'
import {
	__input,
	__searchIcon,
	__cancelIcon,
	__cancelSearch,
	__wrapper,
	__searchWrapper,
	__innerWrapper,
	__errorText,
} from './__style'

const Input = createComponent(__input, 'input', [
	'type',
	'placeholder',
	'icon',
	'value',
	'required',
	'onKeyDown',
	'onBlur',
])
const SearchIconDiv = createComponent(__searchIcon, 'div')
const CancelIconDiv = createComponent(__cancelIcon, 'div')
const CancelSearchDiv = createComponent(__cancelSearch, 'div')
const Wrapper = createComponent(__wrapper, 'div')
const SearchWrapper = createComponent(__searchWrapper, 'div')
const InnerWrapper = createComponent(__innerWrapper, 'div')
const ErrorText = createComponent(__errorText, 'div')

class SearchBar extends React.Component {
	constructor(props) {
		super(props)
		this.debounceEvent = val => this.keyDownEvent(val)
		this.doDebounceEvent = props.renderValueViaProp
			? this.keyDownEvent
			: debounce(this.debounceEvent, 600)
		this.state = {
			searchValue: props.value || '',
			isError: false,
		}
	}

	static getDerivedStateFromProps(props, state) {
		if (props.renderValueViaProp) {
			let tempState = {}
			if (props.value !== state.searchValue) {
				tempState = {
					searchValue: props.value,
				}
			}

			if (props.isError !== state.isError) {
				tempState = {
					...tempState,
					isError: props.isError,
				}
			}

			return tempState
		}

		return null
	}

	keyPress = value => {
		this.setState({
			searchValue: value,
		})
	}

	keyDownEvent = value => {
		if (typeof this.props.handleChange === 'function') {
			this.props.handleChange(value)
		}
	}

	handleClear = () => {
		this.setState({
			searchValue: '',
		})
		if (typeof this.props.handleChange === 'function') {
			this.props.handleChange('')
		}
	}

	cancelSearch = () => {
		if (typeof this.props.cancelSearch === 'function') {
			this.props.cancelSearch()
		}
	}

	handleChange = e => {
		const { pattern } = this.props

		const regexPattern = new RegExp(pattern)
		const result = regexPattern.test(e.target.value)

		if (result) {
			this.doDebounceEvent(e.target.value)
			this.keyPress(e.target.value)
		}
	}

	render() {
		const { searchValue, isError } = this.state
		const {
			placeholder,
			removeCancelButton,
			hideClearIcon,
			zIndex,
			handleBlur,
			errorText,
		} = this.props

		return (
			<Wrapper>
				<InnerWrapper zIndex={zIndex}>
					<SearchWrapper isError={isError}>
						<Input
							value={searchValue}
							type="text"
							placeholder={placeholder}
							onChange={this.handleChange}
							passThrough={['onChange']}
							onBlur={handleBlur}
						/>
						{!searchValue && (
							<SearchIconDiv>
								<Icon glyph={SearchIcon} />
							</SearchIconDiv>
						)}
						{searchValue && !hideClearIcon && (
							<CancelIconDiv>
								<Icon
									glyph={CancelIcon}
									onClick={this.handleClear}
									passThrough={['onClick']}
								/>
							</CancelIconDiv>
						)}
					</SearchWrapper>
					{!removeCancelButton && (
						<CancelSearchDiv
							onClick={this.cancelSearch}
							passThrough={['onClick']}
						>
							Cancel
						</CancelSearchDiv>
					)}
				</InnerWrapper>
				{isError && errorText ? (
					<ErrorText>
						<Icon
							width="14"
							height="14"
							glyph={errorIcon}
							style={{ marginRight: 2 }}
						/>
						{errorText}
					</ErrorText>
				) : null}
			</Wrapper>
		)
	}
}

SearchBar.propTypes = {
	/** Placeholder for the input element */
	placeholder: PropTypes.string,
	/** Icon on the left of input element */
	icon: PropTypes.object,
	/** Required attribute of input element */
	required: PropTypes.bool,
	/** Readonly attribute of input element */
	readOnly: PropTypes.bool,
	/** Value attribute of input element */
	value: PropTypes.string,
	/** Value attribute of input element */
	handleChange: PropTypes.func,
	/** If the cancel CTA on the right is not required */
	removeCancelButton: PropTypes.bool,
	/** hide clear icon */
	hideClearIcon: PropTypes.bool,
	/** zIndex value */
	zIndex: PropTypes.string,
	/** overrider internal states via props */
	renderValueViaProp: PropTypes.bool,
	/** Regex pattern matching for the element */
	pattern: PropTypes.string,
	/** To check if there is any error */
	isError: PropTypes.bool,
	/** To show the error text if any */
	errorText: PropTypes.string,
	/** onBlur Handler of the searchBar */
	handleBlur: PropTypes.func,
}

SearchBar.defaultProps = {
	placeholder: '',
	required: false,
	readOnly: false,
	value: '',
	handleChange: () => {},
	removeCancelButton: false,
	hideClearIcon: false,
	zIndex: '',
	renderValueViaProp: false,
	pattern: '',
	isError: false,
	errorText: '',
	handleBlur: () => {},
}

export default SearchBar
